import axios from 'axios';
import { Message } from 'element-ui';
import store from '../store';
import router from '../router';

// ==================== 错误消息防抖控制 ====================

/**
 * 消息防抖控制器 - 防止批量并发错误时弹窗刷屏
 */
const messageThrottle = {
  // 记录最近显示的消息 { key: 消息标识, time: 时间戳, timer: 定时器 }
  recentMessages: new Map(),
  // 防抖时间窗口（毫秒）
  DEBOUNCE_MS: 1000,

  /**
   * 显示错误消息（带防抖）
   * @param {string} content - 错误内容
   * @param {string} type - 错误类型（用于分组，避免不同类型错误互相覆盖）
   */
  showError(content, type = 'default') {
    const key = `${type}:${content}`;
    const now = Date.now();
    const existing = this.recentMessages.get(key);

    // 如果相同消息在防抖窗口内已显示，跳过
    if (existing && (now - existing.time) < this.DEBOUNCE_MS) {
      // 清除之前的定时器，重新计时
      clearTimeout(existing.timer);
      existing.timer = setTimeout(() => {
        this.recentMessages.delete(key);
      }, this.DEBOUNCE_MS);
      return;
    }

    // 显示消息
    Message.error(content);

    // 记录到队列
    const timer = setTimeout(() => {
      this.recentMessages.delete(key);
    }, this.DEBOUNCE_MS);

    this.recentMessages.set(key, { time: now, timer });
  },

  /**
   * 清理所有定时器
   */
  clear() {
    this.recentMessages.forEach(({ timer }) => clearTimeout(timer));
    this.recentMessages.clear();
  }
};

// 401 跳转锁 - 防止多个并发 401 导致重复跳转
let isRedirectingToLogin = false;

// ==================== Axios 配置 ====================

const request = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
request.interceptors.request.use(
  config => {
    const token = store.getters.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  response => {
    // 如果是 blob 响应（文件下载），直接返回
    if (response.config.responseType === 'blob') {
      return response;
    }

    const res = response.data;

    // 200 和 201 都是成功状态码
    if (res.code !== 200 && res.code !== 201) {
      messageThrottle.showError(res.message || '请求失败', `api-${res.code}`);

      if (res.code === 401) {
        handleUnauthorized(false);
      }

      return Promise.reject(new Error(res.message || '请求失败'));
    }

    return res;
  },
  error => {
    const { response, config } = error;

    if (response && response.status === 401) {
      // 区分登录请求和其他请求的401错误
      const isLoginRequest = config && config.url && config.url.includes('/auth/login');
      handleUnauthorized(isLoginRequest);
    } else if (response && response.status === 403) {
      // 403 权限不足
      messageThrottle.showError('您没有权限访问该接口，如果有需要访问，请向管理员申请', 'auth-403');
    } else {
      const msg = response?.data?.message || error.message || '网络错误';
      messageThrottle.showError(msg, 'network-error');
      error.message = msg;
    }

    return Promise.reject(error);
  }
);

/**
 * 处理 401 未授权错误
 * @param {boolean} isLoginRequest - 是否为登录请求
 */
function handleUnauthorized(isLoginRequest) {
  // 避免重复处理跳转
  if (isRedirectingToLogin) {
    return;
  }

  if (isLoginRequest) {
    // 登录接口返回401，说明用户名或密码错误
    messageThrottle.showError('用户名或者密码错误', 'auth-401-login');
  } else {
    // 其他接口返回401，说明登录状态过期
    isRedirectingToLogin = true;
    messageThrottle.showError('登录已过期，请重新登录', 'auth-401-session');

    // 延迟执行登出和跳转，让当前请求都完成 reject
    setTimeout(() => {
      store.dispatch('logout').finally(() => {
        if (router.currentRoute.path !== '/login') {
          router.push('/login');
        }
        // 重置锁（跳转完成后）
        isRedirectingToLogin = false;
      });
    }, 100);
  }
}

export default request;

// 导出防抖控制器供其他模块使用（如组件销毁时清理）
export { messageThrottle };
